import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Package, Users, Truck, CheckCircle, DollarSign, BarChart3, Clock } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/shared/StatusBadge";
import api from "@/utils/api";
import { Link } from "react-router-dom";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const res = await api.get("/reports/dashboard");
      setData(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const stats = [
    { label: "Total Shipments", value: data?.stats?.total_shipments ?? 0, icon: Package, color: "text-brand-500", bg: "bg-brand-50", border: "border-l-brand-500" },
    { label: "Delivered", value: data?.stats?.delivered ?? 0, icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-50", border: "border-l-emerald-500" },
    { label: "In Transit", value: data?.stats?.in_transit ?? 0, icon: Truck, color: "text-orange-500", bg: "bg-orange-50", border: "border-l-orange-400" },
    { label: "Pending", value: data?.stats?.pending ?? 0, icon: Clock, color: "text-yellow-500", bg: "bg-yellow-50", border: "border-l-yellow-400" },
    { label: "Customers", value: data?.stats?.total_customers ?? 0, icon: Users, color: "text-blue-500", bg: "bg-blue-50", border: "border-l-blue-500" },
    { label: "Agents", value: data?.stats?.total_agents ?? 0, icon: Users, color: "text-purple-500", bg: "bg-purple-50", border: "border-l-purple-500" },
    { label: "Revenue", value: `₹${(data?.stats?.total_revenue || 0).toLocaleString("en-IN")}`, icon: DollarSign, color: "text-teal-500", bg: "bg-teal-50", border: "border-l-teal-500" },
    { label: "Success Rate", value: data?.stats?.total_shipments ? `${Math.round((data.stats.delivered / data.stats.total_shipments) * 100)}%` : "0%", icon: BarChart3, color: "text-indigo-500", bg: "bg-indigo-50", border: "border-l-indigo-500" },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Admin Dashboard</h1>
          <p className="text-slate-500 text-sm">Business overview and key metrics.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              data-testid={`admin-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}
              className={`bg-white rounded-lg p-4 border border-slate-200 border-l-4 ${s.border}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{s.label}</p>
                <div className={`w-7 h-7 ${s.bg} rounded-md flex items-center justify-center`}>
                  <s.icon className={`w-3.5 h-3.5 ${s.color}`} strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-xl font-heading font-bold text-slate-800">{loading ? "—" : s.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Recent Shipments */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h2 className="font-heading font-semibold text-lg text-slate-800 uppercase">Recent Shipments</h2>
            <Link to="/admin/shipments" className="text-sm text-brand-500 font-semibold hover:underline">View All →</Link>
          </div>
          {loading ? (
            <div className="p-6 text-center text-slate-400 text-sm">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {["Order ID", "From", "To", "Weight", "Price", "Status", "Date"].map((h) => (
                      <th key={h} className="px-3 py-2 text-xs font-bold text-slate-400 uppercase text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {(data?.recent_shipments || []).map((s) => (
                    <tr key={s.id} data-testid="admin-shipment-row" className="hover:bg-slate-50">
                      <td className="px-3 py-2"><span className="font-heading font-bold text-brand-500 text-xs">{s.order_id || s.tracking_id}</span></td>
                      <td className="px-3 py-2 text-xs text-slate-600">{s.origin?.city}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{s.destination?.city}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{s.package?.weight}kg</td>
                      <td className="px-3 py-2 text-xs font-semibold text-slate-700">₹{s.price}</td>
                      <td className="px-3 py-2"><StatusBadge status={s.status} /></td>
                      <td className="px-3 py-2 text-xs text-slate-400">{new Date(s.created_at).toLocaleDateString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!data?.recent_shipments || data.recent_shipments.length === 0) && (
                <div className="p-8 text-center text-slate-400 text-sm">No shipments yet.</div>
              )}
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { to: "/admin/customers", label: "Manage Customers", icon: Users },
            { to: "/admin/agents", label: "Manage Agents", icon: Users },
            { to: "/admin/pricing", label: "Pricing Config", icon: DollarSign },
            { to: "/admin/reports", label: "Reports", icon: BarChart3 },
            { to: "/admin/transporter-ids", label: "Transporter IDs", icon: Truck },
          ].map((item) => (
            <Link key={item.to} to={item.to} data-testid={`admin-link-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className="bg-white rounded-lg border border-slate-200 p-4 hover:border-brand-500 hover:shadow-sm transition-all text-center group">
              <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:bg-brand-500 transition-colors">
                <item.icon className="w-5 h-5 text-brand-500 group-hover:text-white transition-colors" strokeWidth={1.5} />
              </div>
              <p className="text-xs font-semibold text-slate-700">{item.label}</p>
            </Link>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
